import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../CommonComponents/RightDrawer";
import ConfirmDeleteModal from "../CommonComponents/Confrimationmodal";
import {
  getRoles,
  getRolePermissions,
  getPermissions,
  addRole,
  deleteRole,
  setRolePermissions,
} from "../../services/roleService";
import { toastAlert } from "../../helpers/toastAlert";
import { hasPermission } from "../../utils/commonutils";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { connect } from "react-redux";
import "./style.css";

const PERMISSION_GROUPS = [
  {
    label: "Customers",
    operations: [
      "create_customer",
      "read_customers",
      "view_customers",
      "update_customer",
      "delete_customer",
    ],
  },
  {
    label: "Employees",
    operations: [
      "create_employee",
      "read_employee",
      "view_employee",
      "update_employee",
      "delete_employee",
    ],
  },
  {
    label: "Tasks",
    operations: [
      "create_task",
      "read_task",
      "view_task",
      "update_task",
      "delete_task",
    ],
  },
  {
    label: "Task threads",
    operations: [
      "create_taskThread",
      "read_taskThread",
      "view_taskThread",
      "update_taskThread",
      "delete_taskThread",
    ],
  },
  {
    label: "Inquiries",
    operations: [
      "create_inquiry",
      "read_inquiry",
      "view_inquiry",
      "update_inquiry",
      "delete_inquiry",
    ],
  },
  {
    label: "Follow-ups",
    operations: [
      "create_followup",
      "read_followup",
      "view_followup",
      "update_followup",
      "delete_followup",
    ],
  },
  {
    label: "Record follow-ups",
    operations: [
      "create_module_followup",
      "read_module_followup",
      "update_module_followup",
      "delete_module_followup",
    ],
  },
  {
    label: "Documents",
    operations: [
      "create_document",
      "read_document",
      "update_document",
      "delete_document",
    ],
  },
  {
    label: "Payment recovery",
    operations: [
      "create_paymentrecovery",
      "read_paymentrecovery",
      "view_paymentrecovery",
      "update_paymentrecovery",
      "delete_paymentrecovery",
    ],
  },
  {
    label: "GST cases",
    operations: [
      "create_casemanagement",
      "read_casemanagement",
      "view_casemanagement",
      "update_casemanagement",
      "delete_casemanagement",
    ],
  },
  {
    label: "Communication",
    operations: [
      "create_communication",
      "read_communication",
      "view_communication",
      "update_communication",
      "delete_communication",
    ],
  },
  {
    label: "Roles",
    operations: ["create_role", "read_role", "update_role", "delete_role", "read_users"],
  },
  {
    label: "Masters",
    operations: ["create_master", "read_master", "update_master", "delete_master"],
  },
];

const ACTION_LABELS = {
  read_users: "View users",
};

const ROLE_HELP = {
  admin: "Full access across the firm, including role setup.",
  manager: "Firm-wide work across modules. Cannot create or delete roles.",
  employee: "General staff: inquiries, customers, tasks, and communication. No Payment recovery or GST files.",
  user: "Same as employee. Use this for staff logins.",
  payment_recovery: "Payment recovery desk: inquiries plus recovery files.",
  gst: "GST desk: inquiries plus GST cases.",
};

const actionLabel = (operation) => {
  if (ACTION_LABELS[operation]) return ACTION_LABELS[operation];
  if (operation.startsWith("create_")) return "Create";
  if (operation.startsWith("update_")) return "Update";
  if (operation.startsWith("delete_")) return "Delete";
  if (operation.startsWith("view_")) return "View detail";
  if (operation.startsWith("read_")) return "View list";
  return operation.replace(/_/g, " ");
};

const RoleComponent = (props) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setAssignedPermissions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedOps, setSelectedOps] = useState([]);
  const [roleName, setRoleName] = useState("");
  const [openRoleModal, setOpenRoleModal] = useState(false);
  const [deleteState, setDeleteState] = useState({ open: false, id: "" });

  const permissionByOperation = useMemo(() => {
    const map = {};
    permissions.forEach((item) => {
      const operation = item?.operation;
      if (!operation || map[operation]) return;
      map[operation] = item;
    });
    return map;
  }, [permissions]);

  const selectedRole = roles.find((role) => String(role._id) === String(selectedRoleId));

  const loadData = async (keepRoleId, { quiet } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const [roleRes, permissionRes, rolePermissionRes] = await Promise.all([
        getRoles(props),
        getPermissions(props),
        getRolePermissions(props),
      ]);
      const nextRoles = roleRes?.data?.roles || [];
      const nextPermissions = permissionRes?.data?.permissions || [];
      const nextRolePermissions = rolePermissionRes?.data?.rolePermissions || [];
      setRoles(nextRoles);
      setPermissions(nextPermissions);
      setAssignedPermissions(nextRolePermissions);
      const nextSelected = keepRoleId || selectedRoleId || nextRoles[0]?._id;
      setSelectedRoleId(nextSelected ? String(nextSelected) : "");
    } catch (error) {
      toastAlert(error?.message || "Could not load roles", "error");
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!selectedRoleId) {
      setSelectedOps([]);
      return;
    }
    const ops = [...new Set(
      (Array.isArray(rolePermissions) ? rolePermissions : [])
        .filter((item) => String(item?.roleId?._id || item?.roleId) === String(selectedRoleId))
        .map((item) => item?.permissionId?.operation)
        .filter(Boolean),
    )];
    setSelectedOps(ops);
  }, [selectedRoleId, rolePermissions]);

  const toggleOperation = (operation) => {
    setSelectedOps((current) =>
      current.includes(operation)
        ? current.filter((item) => item !== operation)
        : [...current, operation],
    );
  };

  const toggleGroup = (operations) => {
    const available = operations.filter((op) => permissionByOperation[op]);
    const allOn = available.every((op) => selectedOps.includes(op));
    setSelectedOps((current) => {
      if (allOn) {
        return current.filter((op) => !available.includes(op));
      }
      return [...new Set([...current, ...available])];
    });
  };

  const handleAddRole = async () => {
    if (!roleName.trim()) {
      toastAlert("Role name is required", "error");
      return;
    }
    const res = await addRole(props, { roleName: roleName.trim().toLowerCase() });
    if (res?.code === 201 || res?.status === 201 || res?.data) {
      toastAlert("Role added", "success");
      setRoleName("");
      setOpenRoleModal(false);
      loadData();
    } else {
      toastAlert(res?.message || "Could not add role", "error");
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    try {
      const permissionIds = selectedOps
        .map((op) => permissionByOperation[op]?._id)
        .filter(Boolean);
      const res = await setRolePermissions(props, selectedRoleId, permissionIds);
      if (res?.code === 200 || res?.status === 200 || res?.data) {
        toastAlert("Permissions saved", "success");
        await loadData(selectedRoleId, { quiet: true });
      } else {
        toastAlert(res?.response?.data?.message || res?.message || "Could not save permissions", "error");
      }
    } catch (error) {
      toastAlert(error?.message || "Could not save permissions", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    await deleteRole(deleteState.id, props);
    toastAlert("Role deleted", "success");
    setDeleteState({ open: false, id: "" });
    setSelectedRoleId("");
    loadData();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3} className="p-2">
      <Grid item md={4} xs={12}>
        <Box className="card-main" p={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Roles</Typography>
            {hasPermission("create_role") && (
              <Button className="btn btn-colored" onClick={() => setOpenRoleModal(true)}>
                + Add role
              </Button>
            )}
          </Box>
          {roles.map((role) => (
            <Box
              key={role._id}
              className={`role-card ${String(selectedRoleId) === String(role._id) ? "active" : ""}`}
              onClick={() => setSelectedRoleId(String(role._id))}
            >
              <Box>
                <Typography fontWeight={600} style={{ textTransform: "capitalize" }}>
                  {role.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {ROLE_HELP[role.name] || "Custom role"}
                </Typography>
              </Box>
              {hasPermission("delete_role") && role.name !== "admin" && (
                <Button
                  color="error"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteState({ open: true, id: role._id });
                  }}
                >
                  Delete
                </Button>
              )}
            </Box>
          ))}
        </Box>
      </Grid>

      <Grid item md={8} xs={12}>
        <Box className="card-main permission-box">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" style={{ textTransform: "capitalize" }}>
                {selectedRole?.name || "Select a role"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Tick what this role can do. Staff only see modules they can access.
              </Typography>
            </Box>
            {hasPermission("update_role") && selectedRole && (
              <Button className="btn btn-colored" disabled={saving} onClick={handleSavePermissions}>
                {saving ? "Saving..." : "Save permissions"}
              </Button>
            )}
          </Box>

          {PERMISSION_GROUPS.map((group) => {
            const available = [...new Set(group.operations)].filter((op) => permissionByOperation[op]);
            if (!available.length) return null;
            const enabledCount = available.filter((op) => selectedOps.includes(op)).length;
            return (
              <Box key={group.label} mb={3} p={2} style={{ border: "1px solid #e5e7eb", borderRadius: 12 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography fontWeight={600}>{group.label}</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip size="small" label={`${enabledCount}/${available.length}`} />
                    {hasPermission("update_role") && (
                      <Button size="small" onClick={() => toggleGroup(available)}>
                        {enabledCount === available.length ? "Clear" : "Select all"}
                      </Button>
                    )}
                  </Box>
                </Box>
                <Grid container>
                  {available.map((operation) => (
                    <Grid item xs={12} sm={6} md={4} key={operation}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedOps.includes(operation)}
                            onChange={() => toggleOperation(operation)}
                            disabled={!hasPermission("update_role")}
                          />
                        }
                        label={actionLabel(operation)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })}
        </Box>
      </Grid>

      <Modal className="all-modals" isOpen={openRoleModal} toggle={() => setOpenRoleModal(false)}>
        <ModalHeader toggle={() => setOpenRoleModal(false)} kicker="Access">Add role</ModalHeader>
        <ModalBody>
          <p className="small-text">Role name</p>
          <TextField
            fullWidth
            size="small"
            placeholder="manager, employee, accountant..."
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
          />
        </ModalBody>
        <ModalFooter>
          <Button className="btn btn-border" onClick={() => setOpenRoleModal(false)}>
            Cancel
          </Button>
          <Button className="btn btn-colored" onClick={handleAddRole}>
            Create role
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmDeleteModal
        open={deleteState.open}
        onClose={() => setDeleteState({ open: false, id: "" })}
        onConfirm={handleDelete}
        message="Delete this role? Users with this role will lose access until they are reassigned."
      />
    </Grid>
  );
};

const mapStateToProps = (state) => ({
  authData: state.authReducer,
});

export default withRouter(connect(mapStateToProps)(RoleComponent));
